const PRODUCT_SLUG_FALLBACK = 'product';
const PRODUCT_DUPLICATE_SEPARATOR = '-';
const DEFAULT_PRODUCT_DEPARTMENT = 'Collection';
const DEFAULT_PRODUCT_SUBCATEGORY = 'Casuals';

export const DEPARTMENT_DISPLAY_ORDER = ['Men', 'Women', 'Kids', 'Clothes', 'Bags'];

export const DEPARTMENT_SUBDIVISION_PRESETS = {
  Men: {
    shoes: ['Casuals', 'Sneakers', 'Officials', 'Loafers', 'Mules', 'Sandals', 'Rubbers'],
    tracksuit: ['Adult Tracksuits', 'Adult Sweaters'],
  },
  Women: {
    shoes: ['Heels', 'Wedges', 'Sandals', 'Sneakers', 'Brogues', 'Flats', 'Loafers', 'Rubbers'],
    tracksuit: ['Adult Tracksuits', 'Adult Sweaters'],
  },
  Kids: {
    shoes: ['Daily Pairs'],
    tracksuit: ['Kids Tracksuits', 'Kids Sweaters'],
  },
  Clothes: {
    shoes: [],
    tracksuit: ['Adult Tracksuits', 'Jeans', 'Kids Tracksuits', 'Adult Sweaters', 'Kids Sweaters'],
  },
  Bags: {
    shoes: ['Handbags', 'Crossbody Bags', 'Backpacks', 'Tote Bags', 'Travel Bags', 'Laptop Bags'],
    tracksuit: [],
  },
};

export const slugifyProductName = (value = '') => {
  const normalizedValue = String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

  return normalizedValue || PRODUCT_SLUG_FALLBACK;
};

export const slugifyRouteSegment = slugifyProductName;

const normalizeRouteLabel = (value, fallback) => {
  const normalizedValue = String(value || '').trim();
  return normalizedValue || fallback;
};

const normalizeSizeType = (value = '') => String(value || '').trim().toLowerCase();

const normalizeDepartmentName = (value = '') => {
  const normalizedValue = String(value || '').trim();
  const lowerValue = normalizedValue.toLowerCase();

  if (lowerValue === 'men') return 'Men';
  if (lowerValue === 'women' || lowerValue === 'ladies') return 'Women';
  if (lowerValue === 'kids' || lowerValue === 'kid' || lowerValue === 'children') return 'Kids';
  if (lowerValue === 'clothes' || lowerValue === 'clothing' || lowerValue === 'apparel' || lowerValue === 'apparels') {
    return 'Clothes';
  }
  if (
    lowerValue === 'bags' ||
    lowerValue === 'bag' ||
    lowerValue === 'handbag' ||
    lowerValue === 'handbags' ||
    lowerValue === 'backpack' ||
    lowerValue === 'backpacks'
  ) {
    return 'Bags';
  }

  return normalizedValue;
};

const getProductAudience = (product = {}) =>
  normalizeDepartmentName(product?.category || product?.audience || product?.department) || 'Men';

const isClothingProduct = ({ department = '', sizeType = '' } = {}) =>
  normalizeDepartmentName(department) === 'Clothes' || normalizeSizeType(sizeType) === 'tracksuit';

const getCatalogDepartment = ({ department = '', sizeType = '' } = {}) => {
  if (isClothingProduct({ department, sizeType })) {
    return 'Clothes';
  }

  return normalizeDepartmentName(department) || DEFAULT_PRODUCT_DEPARTMENT;
};

const getNormalizedDepartment = (value = '') =>
  getCatalogDepartment({ department: normalizeRouteLabel(value, DEFAULT_PRODUCT_DEPARTMENT) });

export const getSubdivisionOptions = ({ department, sizeType } = {}) => {
  const normalizedDepartment = getCatalogDepartment({ department, sizeType });
  const normalizedSizeType = normalizeSizeType(sizeType);
  const presetGroup =
    DEPARTMENT_SUBDIVISION_PRESETS[normalizedDepartment] ||
    DEPARTMENT_SUBDIVISION_PRESETS[DEFAULT_PRODUCT_DEPARTMENT] ||
    null;

  if (!presetGroup) {
    return normalizedSizeType === 'tracksuit' ? [] : [DEFAULT_PRODUCT_SUBCATEGORY];
  }

  if (normalizedDepartment === 'Clothes') {
    return presetGroup.tracksuit || [];
  }

  if (normalizedSizeType === 'tracksuit') {
    return presetGroup.tracksuit || [];
  }

  return presetGroup.shoes || [DEFAULT_PRODUCT_SUBCATEGORY];
};

export const getExplicitSubdivision = ({
  category,
  department,
  sizeType,
  currentValue,
} = {}) => {
  const normalizedValue = String(currentValue || '').trim();
  const lowerValue = normalizedValue.toLowerCase();
  const normalizedSizeType = normalizeSizeType(sizeType);
  const suggestedSubdivisions = getSubdivisionOptions({
    department: category || department,
    sizeType,
  });

  if (
    !normalizedValue ||
    lowerValue === 'subcategory' ||
    lowerValue === 'subdivision' ||
    (normalizedSizeType === 'tracksuit' &&
      ['tracksuit', 'tracksuits', 'clothes', 'clothing', 'apparel', 'apparels'].includes(lowerValue)) ||
    (normalizedSizeType !== 'tracksuit' && ['shoe', 'shoes'].includes(lowerValue))
  ) {
    return '';
  }

  const matchedSubdivision = suggestedSubdivisions.find(
    (option) => option.toLowerCase() === lowerValue
  );

  return matchedSubdivision || normalizedValue;
};

export const resolveSubdivisionForForm = ({
  category,
  department,
  sizeType,
  currentValue,
  audience,
} = {}) =>
  getExplicitSubdivision({ category, department, sizeType, currentValue }) ||
  getDefaultSubdivision({
    category: category || department,
    audience,
    sizeType,
  });

export const getVisibleSubdivisionOptions = ({ department, sizeType } = {}) => {
  const normalizedDepartment = getCatalogDepartment({ department, sizeType });

  if (normalizedDepartment === 'Kids') {
    return [];
  }

  return getSubdivisionOptions({ department: normalizedDepartment, sizeType });
};

const productUsesSubcategories = (product = {}) =>
  getSubdivisionOptions({
    department: getProductDepartment(product),
    sizeType: product?.sizeType,
  }).length > 0;

const matchSubdivisionByKeywords = (content = '', rules = []) =>
  rules.find((rule) => rule.keywords.some((keyword) => content.includes(keyword)))?.label || '';

const matchSubdivisionByKeywordSources = (sources = [], rules = []) => {
  for (const source of sources) {
    const matchedSubdivision = matchSubdivisionByKeywords(source, rules);
    if (matchedSubdivision) {
      return matchedSubdivision;
    }
  }

  return '';
};

const inferSubdivisionFromProduct = (product = {}) => {
  const primaryContent = [
    product?.name,
    product?.title,
    product?.description,
    product?.category,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  const legacySubdivisionContent = [product?.subcategory, product?.subdivision]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  const contentSources = [primaryContent, legacySubdivisionContent].filter(Boolean);

  if (!contentSources.length) return '';

  const department = getProductDepartment(product);
  const sizeType = normalizeSizeType(product?.sizeType);
  const audience = getProductAudience(product);

  if (department === 'Kids') {
    return 'Daily Pairs';
  }

  if (department === 'Clothes' || sizeType === 'tracksuit') {
    const joinedContent = contentSources.join(' ');

    if (/\bjeans?\b/.test(joinedContent)) {
      return 'Jeans';
    }

    const audienceLabel =
      audience === 'Kids' || /\b(kid|kids|child|children|boys|girls|school)\b/.test(joinedContent)
        ? 'Kids'
        : 'Adult';
    const clothingType = /\b(sweater|sweaters|jumper|jumpers|hoodie|hoodies|sweatshirt|sweatshirts)\b/.test(joinedContent)
      ? 'Sweaters'
      : 'Tracksuits';

    return `${audienceLabel} ${clothingType}`;
  }

  if (department === 'Men') {
    return (
      matchSubdivisionByKeywordSources(contentSources, [
        { label: 'Loafers', keywords: ['loafer', 'loafers', 'moccasin', 'moccasins'] },
        { label: 'Mules', keywords: ['mule', 'mules', 'clog', 'clogs', 'backless'] },
        { label: 'Sandals', keywords: ['sandal', 'sandals', 'fisherman', 'gladiator'] },
        {
          label: 'Rubbers',
          keywords: [
            'rubbers',
            'slipper',
            'slippers',
            'flip flop',
            'flip flops',
            'flip-flop',
            'flip-flops',
            'flipflop',
            'flipflops',
            'slider',
            'sliders',
            'slide',
            'slides',
          ],
        },
        { label: 'Sneakers', keywords: ['sneaker', 'sneakers', 'trainer', 'trainers', 'runner', 'runners'] },
        {
          label: 'Officials',
          keywords: [
            'official',
            'officials',
            'formal',
            'oxford',
            'oxfords',
            'derby',
            'derbies',
            'brogue',
            'brogues',
            'dress shoe',
            'dress shoes',
            'monk',
            'monk strap',
            'monk straps',
          ],
        },
        {
          label: 'Casuals',
          keywords: [
            'casual',
            'casuals',
            'boot',
            'boots',
            'boat shoe',
            'boat shoes',
            'canvas',
          ],
        },
      ]) || 'Casuals'
    );
  }

  if (department === 'Women') {
    return (
      matchSubdivisionByKeywordSources(contentSources, [
        { label: 'Heels', keywords: ['heel', 'heels', 'pump', 'pumps', 'stiletto'] },
        { label: 'Wedges', keywords: ['wedge', 'wedges'] },
        { label: 'Sandals', keywords: ['sandal', 'sandals', 'strappy'] },
        { label: 'Sneakers', keywords: ['sneaker', 'sneakers', 'trainer', 'trainers', 'runner', 'runners'] },
        { label: 'Loafers', keywords: ['loafer', 'loafers', 'moccasin', 'moccasins'] },
        { label: 'Brogues', keywords: ['brogue', 'brogues', 'oxford', 'oxfords'] },
        {
          label: 'Rubbers',
          keywords: [
            'rubbers',
            'slipper',
            'slippers',
            'flip flop',
            'flip flops',
            'flip-flop',
            'flip-flops',
            'flipflop',
            'flipflops',
            'slider',
            'sliders',
          ],
        },
        { label: 'Flats', keywords: ['flat', 'flats', 'ballerina', 'mule', 'mules', 'boot', 'boots', 'clog', 'clogs'] },
      ]) || 'Flats'
    );
  }

  if (department === 'Bags') {
    return (
      matchSubdivisionByKeywordSources(contentSources, [
        { label: 'Laptop Bags', keywords: ['laptop', 'office bag', 'work bag', 'messenger bag'] },
        { label: 'Travel Bags', keywords: ['travel', 'duffel', 'weekender', 'carry on', 'carry-on'] },
        { label: 'Backpacks', keywords: ['backpack', 'rucksack', 'school bag'] },
        { label: 'Crossbody Bags', keywords: ['crossbody', 'sling bag', 'shoulder bag'] },
        { label: 'Tote Bags', keywords: ['tote', 'shopper bag'] },
        { label: 'Handbags', keywords: ['handbag', 'purse', 'satchel', 'mini bag'] },
      ]) || 'Handbags'
    );
  }

  return matchSubdivisionByKeywordSources(contentSources, [
    { label: 'Casuals', keywords: ['casual', 'casuals'] },
    { label: 'Sneakers', keywords: ['sneaker', 'sneakers'] },
  ]);
};

export const getDefaultSubdivision = (product = {}) => {
  const department = getProductDepartment(product);
  const sizeType = normalizeSizeType(product?.sizeType);
  const suggestedOptions = getSubdivisionOptions({ department, sizeType });

  if (!suggestedOptions.length) {
    return '';
  }

  if (department === 'Clothes') {
    return getProductAudience(product) === 'Kids' ? 'Kids Tracksuits' : 'Adult Tracksuits';
  }

  return suggestedOptions[0] || (sizeType === 'tracksuit' ? '' : DEFAULT_PRODUCT_SUBCATEGORY);
};

export const getProductSlug = (productOrName) => {
  if (typeof productOrName === 'string') {
    return slugifyProductName(productOrName);
  }

  return slugifyProductName(productOrName?.slug || productOrName?.name || productOrName?.title || '');
};

export const getProductDepartment = (product = {}) =>
  getCatalogDepartment({
    department: normalizeRouteLabel(product?.department || product?.category, DEFAULT_PRODUCT_DEPARTMENT),
    sizeType: product?.sizeType,
  });

export const getProductSubdivision = (product = {}) => {
  if (!productUsesSubcategories(product)) {
    return '';
  }

  return (
    getExplicitSubdivision({
      category: product?.category || product?.department,
      sizeType: product?.sizeType,
      currentValue: product?.subcategory || product?.subdivision,
    }) ||
    inferSubdivisionFromProduct(product) ||
    getDefaultSubdivision(product)
  );
};

export const getProductDepartmentSlug = (valueOrProduct) =>
  (() => {
    const resolvedValue =
      typeof valueOrProduct === 'string' ? String(valueOrProduct || '').trim() : getProductDepartment(valueOrProduct);

    return resolvedValue ? slugifyRouteSegment(resolvedValue) : '';
  })();

export const getProductSubdivisionSlug = (valueOrProduct) =>
  (() => {
    const resolvedValue =
      typeof valueOrProduct === 'string' ? String(valueOrProduct || '').trim() : getProductSubdivision(valueOrProduct);

    return resolvedValue ? slugifyRouteSegment(resolvedValue) : '';
  })();

export const getProductVariantOptions = (product = {}) =>
  (Array.isArray(product?.variants) ? product.variants : []).filter(Boolean);

export const getProductVariantSlug = (valueOrVariant = '') => {
  const resolvedValue =
    typeof valueOrVariant === 'string'
      ? String(valueOrVariant || '').trim()
      : String(valueOrVariant?.name || '').trim();

  return resolvedValue ? slugifyRouteSegment(resolvedValue) : '';
};

export const findProductVariant = (product = {}, valueOrVariant = '') => {
  const requestedValue =
    typeof valueOrVariant === 'string'
      ? String(valueOrVariant || '').trim()
      : String(valueOrVariant?.name || '').trim();

  if (!requestedValue) return null;

  const requestedSlug = getProductVariantSlug(requestedValue);
  const requestedLowerValue = requestedValue.toLowerCase();

  return (
    getProductVariantOptions(product).find((variant) => {
      const variantName = String(variant?.name || '').trim();
      if (!variantName) return false;

      return (
        variantName.toLowerCase() === requestedLowerValue ||
        getProductVariantSlug(variantName) === requestedSlug
      );
    }) || null
  );
};

export const formatProductCategory = (product = {}) => {
  const department = getProductDepartment(product);
  const subdivision = getProductSubdivision(product);

  if (department === 'Kids') {
    return department;
  }

  if (!subdivision || getProductSubdivisionSlug(subdivision) === getProductDepartmentSlug(department)) {
    return department;
  }

  return `${department} / ${subdivision}`;
};

const compareProductsForSlugging = (left, right) => {
  const leftId = String(left?.id || '');
  const rightId = String(right?.id || '');
  return leftId.localeCompare(rightId);
};

const attachRouteMetadata = (product = {}) => {
  const department = getProductDepartment(product);
  const subcategory = getProductSubdivision(product);

  return {
    ...product,
    category: normalizeRouteLabel(product?.category || product?.department, department),
    department,
    departmentSlug: getProductDepartmentSlug(department),
    subcategory,
    subdivision: subcategory,
    subcategorySlug: getProductSubdivisionSlug(subcategory),
  };
};

export const attachProductSlugs = (products = []) => {
  const sortedProducts = [...products].filter((product) => product?.id).sort(compareProductsForSlugging);
  const slugCounts = new Map();
  const slugById = new Map();

  sortedProducts.forEach((product) => {
    const baseSlug = getProductSlug(product);
    const currentCount = (slugCounts.get(baseSlug) || 0) + 1;
    slugCounts.set(baseSlug, currentCount);

    const resolvedSlug =
      currentCount === 1 ? baseSlug : `${baseSlug}${PRODUCT_DUPLICATE_SEPARATOR}${currentCount}`;

    slugById.set(String(product.id), resolvedSlug);
  });

  return products.map((product) => {
    if (!product?.id) return attachRouteMetadata(product);

    return attachRouteMetadata({
      ...product,
      slug: slugById.get(String(product.id)) || getProductSlug(product),
    });
  });
};

export const buildProductLookup = (products = []) => {
  const sluggedProducts = attachProductSlugs(products);
  const productById = new Map();
  const productBySlug = new Map();

  sluggedProducts.forEach((product) => {
    if (!product?.id) return;

    productById.set(String(product.id), product);

    if (product.slug) {
      productBySlug.set(product.slug, product);
    }
  });

  return {
    products: sluggedProducts,
    productById,
    productBySlug,
  };
};

export const buildCatalogPath = ({ department, subcategory } = {}) => {
  const departmentSlug = department ? getProductDepartmentSlug(department) : '';
  const visibleSubcategories = getVisibleSubdivisionOptions({ department });
  const subcategorySlug =
    subcategory && visibleSubcategories.length ? getProductSubdivisionSlug(subcategory) : '';

  if (!departmentSlug) return '/products';
  if (!subcategorySlug) return `/products/${departmentSlug}`;

  return `/products/${departmentSlug}/${subcategorySlug}`;
};

export const buildProductPath = (product, options = {}) => {
  if (!product?.id) return '/products';

  const resolvedSlug = product?.slug || getProductSlug(product);
  if (!resolvedSlug) return '/products';

  const departmentSlug = getProductDepartmentSlug(product);
  const subcategorySlug = getProductSubdivisionSlug(product);

  const basePath = !subcategorySlug
    ? `/products/${departmentSlug}/${resolvedSlug}`
    : `/products/${departmentSlug}/${subcategorySlug}/${resolvedSlug}`;
  const matchedVariant = findProductVariant(product, options?.color);
  const variantSlug = getProductVariantSlug(matchedVariant || options?.color);

  return variantSlug ? `${basePath}/${variantSlug}` : basePath;
};

export const buildProductLandingPath = (product, options = {}) => {
  const productPath = buildProductPath(product, options);
  return productPath === '/products' ? '/landing/products' : `/landing${productPath}`;
};

export const buildHotSaleLandingPath = (product, options = {}) => {
  const productPath = buildProductPath(product, options);
  return productPath === '/products' ? '/Hotdeals/products' : `/Hotdeals${productPath}`;
};

export const buildProductVariantPaths = (product) =>
  getProductVariantOptions(product)
    .map((variant) => buildProductPath(product, { color: variant?.name }))
    .filter(Boolean)
    .filter((path, index, paths) => paths.indexOf(path) === index);

export const buildLegacyProductPath = (productId = '') =>
  `/product/${encodeURIComponent(String(productId).trim())}`;

export const buildDeprecatedProductPath = (product) => {
  if (!product?.id) return buildProductPath(product);

  return `/product/${product?.slug || getProductSlug(product)}/${encodeURIComponent(product.id)}`;
};

export const buildCatalogHierarchy = (products = []) => {
  const sluggedProducts = attachProductSlugs(products);
  const departmentOrder = new Map(DEPARTMENT_DISPLAY_ORDER.map((department, index) => [department, index]));
  const departmentMap = new Map(
    DEPARTMENT_DISPLAY_ORDER.map((department) => {
      const subcategories = getVisibleSubdivisionOptions({
        department,
        sizeType: department === 'Clothes' ? 'tracksuit' : 'shoes',
      }).map((subcategory) => ({
        name: subcategory,
        slug: getProductSubdivisionSlug(subcategory),
        count: 0,
      }));

      return [
        getProductDepartmentSlug(department),
        {
          name: department,
          slug: getProductDepartmentSlug(department),
          count: 0,
          subcategories,
        },
      ];
    })
  );

  sluggedProducts.forEach((product) => {
    if (!product?.id) return;

    const departmentSlug = product.departmentSlug || getProductDepartmentSlug(product);
    const subcategorySlug = product.subcategorySlug || getProductSubdivisionSlug(product);
    const departmentName = product.department || getProductDepartment(product);
    const subcategoryName = product.subcategory || getProductSubdivision(product);
    const visibleSubcategories = getVisibleSubdivisionOptions({
      department: departmentName,
      sizeType: product?.sizeType,
    });

    if (!departmentMap.has(departmentSlug)) {
      departmentMap.set(departmentSlug, {
        name: departmentName,
        slug: departmentSlug,
        count: 0,
        subcategories: [],
      });
    }

    const department = departmentMap.get(departmentSlug);
    department.count += 1;

    if (!subcategorySlug || !visibleSubcategories.length) {
      return;
    }

    let subcategory = department.subcategories.find((entry) => entry.slug === subcategorySlug);

    if (!subcategory) {
      subcategory = {
        name: subcategoryName,
        slug: subcategorySlug,
        count: 0,
      };
      department.subcategories.push(subcategory);
    }

    subcategory.count += 1;
  });

  return Array.from(departmentMap.values())
    .map((department) => ({
      ...department,
      subcategories: department.subcategories.sort((left, right) => {
        const presetOrder = getVisibleSubdivisionOptions({
          department: department.name,
          sizeType: department.name === 'Clothes' ? 'tracksuit' : 'shoes',
        });
        const leftIndex = presetOrder.indexOf(left.name);
        const rightIndex = presetOrder.indexOf(right.name);

        if (leftIndex !== -1 || rightIndex !== -1) {
          if (leftIndex === -1) return 1;
          if (rightIndex === -1) return -1;
          return leftIndex - rightIndex;
        }

        return left.name.localeCompare(right.name);
      }),
    }))
    .sort((left, right) => {
      const leftIndex = departmentOrder.get(left.name);
      const rightIndex = departmentOrder.get(right.name);

      if (leftIndex !== undefined || rightIndex !== undefined) {
        if (leftIndex === undefined) return 1;
        if (rightIndex === undefined) return -1;
        return leftIndex - rightIndex;
      }

      return left.name.localeCompare(right.name);
    });
};

export const filterProductsByCatalog = (products = [], { departmentSlug, subcategorySlug } = {}) =>
  attachProductSlugs(products).filter((product) => {
    if (departmentSlug && product.departmentSlug !== departmentSlug) {
      return false;
    }

    if (subcategorySlug && product.subcategorySlug !== subcategorySlug) {
      return false;
    }

    return true;
  });

export const resolveLegacyProduct = (products = [], pathSegments = []) => {
  const normalizedSegments = Array.isArray(pathSegments) ? pathSegments.filter(Boolean) : [];
  if (!normalizedSegments.length || normalizedSegments.length > 2) return null;

  const { productById, productBySlug } = buildProductLookup(products);
  const requestedPrimarySegment = normalizedSegments[0];
  const requestedIdSegment = normalizedSegments.length === 2 ? normalizedSegments[1] : null;

  return (
    (requestedIdSegment ? productById.get(requestedIdSegment) : null) ||
    productById.get(requestedPrimarySegment) ||
    productBySlug.get(requestedPrimarySegment) ||
    null
  );
};
